import { useCallback, useEffect } from 'react';

import { request } from '@/renderer/utils/api';
import { asyncLimit } from '@/renderer/utils/async';
import { useLocalStorage } from '@/renderer/utils/hooks/useLocalStorage';
import { useData } from '@/renderer/utils/useData';
import type { RequestSchema } from '@/types/request';

import { areObjectsDeeplyDifferent } from '@/pages/order/components/productHelpers';
import { dispatchShowAlert } from './alerts';
import { useRefUpdating } from './hooks/useRefUpdating';
import { IS_SERVER } from './ssr';
import { useRasContext } from './useRasContext';
import useRequest from './useRequest';
import useWebSocket, { WS_BASE_URL } from './useWebSocket2';
import type { Connection } from './ws/types';

export const userRequestSchema: RequestSchema<API.User> = {
  name: 'userRequest',
  input: '/api/v1/user',
};

export const authRefreshRequestSchema: RequestSchema<API.RefreshState, { token: string }> = {
  name: 'authRefreshRequestSchema',
  input: '/api/v1/auth/refresh',
  method: 'POST',
};

export const enum AuthRefreshResult {
  SKIPPED,
  SUCCESS,
  LOGGEDOUT,
  EXPIRED,
  ERROR,
}

// Refresh a token 2 min before expiry if possible; it's still doable after, but this keeps things lively
const EARLY_REFRESH_MS = 2 * 60 * 1000;

let nextRefresh = 0;

if (!IS_SERVER) {
  parseToken(authToken());
}

const NAH = { run: () => undefined };
export interface CustomEventMap {
  'ras:forceLogout': CustomEvent;
}

interface ForceLogoutEventTarget extends EventTarget {
  addEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | AddEventListenerOptions,
  ): void;
  dispatchEvent<K extends keyof CustomEventMap>(ev: CustomEventMap[K]): boolean;
  removeEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | EventListenerOptions,
  ): void;
}

export const forceLogoutEventTarget: ForceLogoutEventTarget = new EventTarget();

export default function useMe(
  withUpdater: true,
  withFetcher: boolean,
): [API.User | undefined | null, (newUser: API.User | null) => void, () => void];
export default function useMe(
  withUpdater: boolean,
  withFetcher: true,
): [API.User | undefined | null, (newUser: API.User | null) => void, () => void];
export default function useMe(
  withUpdater?: false | undefined,
  withFetcher?: false | undefined,
): API.User | undefined | null;
export default function useMe(withUpdater?: boolean, withFetcher?: boolean) {
  // const localAuthToken = useAuthToken();
  const rasContext = useRasContext();

  const { run } = withFetcher
    ? // biome-ignore lint/correctness/useHookAtTopLevel: Never alter the withFetcher boolean!
      useRequest(userRequestSchema, { hideAlert: true, manual: true })
    : NAH;

  const userRef = useRefUpdating(rasContext?.data?.user);
  const setUserRef = useRefUpdating(rasContext && 'setUser' in rasContext && rasContext.setUser);

  const setUser = useCallback((newUser: API.User | null, newToken?: string) => {
    if (setUserRef.current && areObjectsDeeplyDifferent(userRef.current, newUser)) {
      setUserRef.current(newUser, newToken);
    }
  }, []);

  const refetchUser = useCallback(async () => {
    const newUser = await run();

    if (newUser !== undefined) {
      setUser(newUser);
    }

    return newUser;
  }, [run, setUser]);

  // useUpdateEffect(() => {
  //   if (!localAuthToken && setUserRef.current) {
  //     setUserRef.current(null);
  //   }
  // }, [localAuthToken]);

  return withUpdater || withFetcher
    ? [rasContext.data?.user, setUser, refetchUser]
    : rasContext.data?.user;
}

export function PageTokenRefresher() {
  const authTokenValue = useAuthToken();
  const [me, , refetchUser] = useMe(false, true);

  const onWatcherClose = useCallback(function (
    this: Connection,
    event: Event & { reason: string },
  ) {
    if (
      `${event?.reason}` === '401' ||
      `${event?.reason}` === '402' ||
      `${event?.reason}` === '403'
    ) {
      // WS says we are logged out
      if (authToken()) {
        authToken(null);

        // @todo should we do this? we should probably notify the user that they were logged out somehow
        // setUser(null);
      }

      this.close();
    }
  }, []);

  const { ready } = useWebSocket(WS_BASE_URL, {
    connect: !!authTokenValue,
    onClose: onWatcherClose,
  });

  const meRef = useRefUpdating(me);
  const refetchUserRef = useRefUpdating(refetchUser);

  useEffect(() => {
    if (meRef.current === null) {
      // null means the server knows our session is expired or we're logged out.
      // undefined means the server doesn't know if we're logged in or not, it failed to fetch
      if (authToken()) {
        authToken(null);
      }
    } else {
      // The first time this gets used anywhere on the client, we should ensure that we get the authToken in localStorage
      refreshToken({ forceIfUnknown: true, hideExpiredError: true });
    }
  }, []);

  useEffect(() => {
    if (ready && meRef.current === undefined) {
      // In the event that server was down upon page load, WS should not be connected and user should be undefined
      // When we regain WS connection, user won't have been fetched yet (undefined). We can fetch it now.
      refetchUserRef.current?.();
    }
  }, [ready]);

  return null;
}

export function ForceLogoutWatcher() {
  const { setUser } = useRasContext();

  // @todo investigate if we should do setUser?.(null, null) to remove authToken too
  const logoutCallback = useCallback(() => setUser?.(null), [setUser]);

  useEffect(() => {
    // Only happens client-side
    forceLogoutEventTarget.addEventListener('ras:forceLogout', logoutCallback, false);

    return () => {
      forceLogoutEventTarget.removeEventListener('ras:forceLogout', logoutCallback, false);
    };
  }, [logoutCallback]);

  return null;
}

function parseToken(token: string | null | undefined) {
  try {
    if (token) {
      const oldData: API.SessionToken = JSON.parse(atob(token.split('.')[1]));

      if (oldData?.expires) {
        let setNextRefresh = oldData.expires * 1000 - EARLY_REFRESH_MS;

        if (setNextRefresh <= Date.now()) {
          // Possible due to a misconfiguration of the server and the client-side early refresh time
          setNextRefresh = oldData.expires * 1000;
        }

        if (!IS_SERVER) {
          nextRefresh = setNextRefresh;
        }

        return {
          ...oldData,
          nextRefresh: setNextRefresh,
        };
      }
    } else {
      if (!IS_SERVER) {
        nextRefresh = 0;
      }
    }
  } catch (_error) {
    //
  }
}

let firstTime = true;

// @todo this method's EXPIRED and ERROR results won't prevent subsequent attempts at refresh; handle that
export const refreshToken = asyncLimit(async function refreshToken2({
  forceIfLoggedOut,
  forceIfUnknown,
  forceIfEarly,
  hideExpiredError,
  submessage,
  fetch: fetcher,
  authToken: givenAuthToken,
}: {
  forceIfLoggedOut?: boolean;
  forceIfUnknown?: boolean;
  forceIfEarly?: boolean;
  hideExpiredError?: boolean;
  submessage?: string | undefined;
  fetch?: typeof fetch;
  authToken?: string | null | undefined;
} = {}) {
  const now = Date.now();

  // eslint-disable-next-line no-async-promise-executor
  let tokenData: ReturnType<typeof parseToken> | undefined | null;
  let tokenRaw: string | null | undefined = givenAuthToken;

  if (tokenRaw) {
    tokenData = parseToken(tokenRaw);
  } else if (nextRefresh === undefined && !IS_SERVER) {
    tokenRaw = authToken();
    tokenData = parseToken(tokenRaw);
  }

  const realNextRefresh = IS_SERVER ? tokenData?.nextRefresh : nextRefresh;

  if (realNextRefresh) {
    // User is definitely logged in.
    if (!IS_SERVER) {
      firstTime = false;
    }

    if (realNextRefresh > now && !forceIfEarly) {
      // We can wait until the next refresh time
      return { token: tokenRaw, status: AuthRefreshResult.SKIPPED };
    }
  } else if (!IS_SERVER && tokenRaw) {
    // The token seems to be unrefreshable, we should flag them as logged out
    firstTime = false;

    if (!forceIfLoggedOut) {
      return { token: tokenRaw, status: AuthRefreshResult.LOGGEDOUT };
    }
  } else if (!IS_SERVER && firstTime) {
    // We don't know if this person is logged in yet, it's the first time run
    firstTime = false;

    if (!forceIfUnknown) {
      return { token: tokenRaw, status: AuthRefreshResult.LOGGEDOUT };
    }
  } else if (!IS_SERVER) {
    return { token: tokenRaw, status: AuthRefreshResult.LOGGEDOUT };
  } // else: IS_SERVER or has token. For IS_SERVER, we will allow the refresh no matter what.

  try {
    const promise = request(authRefreshRequestSchema, {
      body: { token: tokenData?.refresh || 'autodetect' },
      skipRefresh: true,
      hideAlert: true,
      fetch: fetcher,
      authToken: tokenRaw,
    });

    const result = await promise;

    if (result.message === 'API.AUTHENTICATION.REFRESH.SUCCESS') {
      if (!IS_SERVER) {
        parseToken(result.session.token);
        authToken(result.session.token);
      }

      return { token: result.session.token, status: AuthRefreshResult.SUCCESS };
    } else {
      if (!hideExpiredError) {
        dispatchShowAlert({
          id: 'refreshToken_expired',
          status: 'error',
          message: 'Your session has expired. Please log in again.',
          submessage: result.message,
        });
      }

      authToken(null);

      return { token: tokenRaw, status: AuthRefreshResult.EXPIRED };
    }
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);

    if (msg === 'API.AUTHENTICATION.REFRESH.EXPIRED') {
      if (!hideExpiredError) {
        dispatchShowAlert({
          id: 'refreshToken_expired',
          status: 'error',
          message: 'Your session has expired. Please log in again.',
          submessage,
        });
      }

      authToken(null);

      return { token: tokenRaw, status: AuthRefreshResult.EXPIRED };
    }

    if (error instanceof TypeError) {
      console.error(error);

      dispatchShowAlert({
        id: 'refreshToken_error',
        status: 'warning',
        message: `We ran into a network issue while refreshing your session. You may need to reload the page. Error: ${msg}`,
        submessage,
      });
    } else {
      dispatchShowAlert({
        id: 'refreshToken_error',
        status: 'error',
        message: `You may need to reload. Session refresh error: ${msg}`,
        submessage,
      });
    }

    return { token: tokenRaw, status: AuthRefreshResult.ERROR };
  }
}, 1);

export function authToken(newToken?: string | null): string | null | undefined {
  if (IS_SERVER) {
    return ''; // @todo
  }

  if (newToken !== undefined) {
    // Set new value
    try {
      const oldValue = localStorage.authToken;

      if (newToken === null) {
        // Null means delete; reserved for logout or session expiry detection
        localStorage.removeItem('authToken');

        parseToken(null);
      } else {
        // New token to be saved
        localStorage.setItem('authToken', JSON.stringify(newToken));

        parseToken(newToken);
      }

      if (oldValue !== newToken) {
        // This event only triggers when another tab causes it, so we need to have it dispatched on the current page too
        const e = new StorageEvent('storage', {
          storageArea: window.localStorage,
          key: 'authToken',
          oldValue,
          newValue: newToken,
          url: window.location.href,
        });

        window.dispatchEvent(e);
      }
    } catch (error) {
      console.log(error);
    }
  }

  return localStorage.authToken;
}

export function useAuthToken() {
  const data = useData();
  const [localAuthToken] = useLocalStorage('authToken', data?.authToken || '');

  return localAuthToken;
}
